
import {
  defineComponent,
  Ref,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useDriveStore } from '../../../../modules/drive/store/driveStore';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import { CHOOSER_TYPE, MEDIA_TYPES, UPLOAD_PLACES } from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { useFlutterBus } from '@/store/flutterBus';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import {
  ICaseFilesManage,
  IFileUpload, IImageConverted, IImageCrop, ISearchLabelInput,
} from '@/types/interfaces';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import useFlutter from '@/composables/UseFlutter';
import DriveAddPhotos from '@/modules/drive/components/DriveAddPhotos.vue';
import DriveAddFiles from '@/modules/drive/components/DriveAddFiles.vue';
import { bmpConvertor, bmpConvertorToFile, convertBmpInFileList } from '@/helpers/bmpConvertor';
import { blobToUrl, getVideoThumbnail } from '@/helpers/videoThumbnail';
// import DriveMenuPopUp from '@/modules/drive/components/UI/DriveMenuPopUp.vue';
import DriveAddMenuPopUp from '@/modules/drive/components/UI/DriveAddMenuPopUp.vue';
import DriveDesktopAddFiles from '@/modules/drive/components/desktop/cases/DriveDesktopAddFiles.vue';
import DriveDesktopAddPhotos from '@/modules/drive/components/desktop/cases/DriveDesktopAddPhotos.vue';

export default defineComponent({
  props: {
    customControl: { type: Boolean, default: false },
  },
  emits: ['uploadPhotos', 'addPhotos', 'uploadFiles', 'addFiles', 'close'],
  components: {
    DriveDesktopAddFiles,
    BottomPopUpItem,
    AppBottomPopUp,
    AppTakePhoto,
    DriveAddPhotos,
    DriveAddFiles,
    // DriveMenuPopUp,
    DriveAddMenuPopUp,
    DriveDesktopAddPhotos,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();

    const fileInput: Ref<HTMLInputElement | null> = ref(null);
    const photoInput: Ref<HTMLInputElement | null> = ref(null);

    const mediaToCrop = ref({} as IImageCrop);
    const mediaInputRef = ref();
    const mediaLoadedKey = ref(0);
    const appTakePhoto = ref();
    const posData = ref({ x: 98, y: 124 });
    const positionData = ref({
      x: 727,
    });
    const screenWidth = ref({
      x: 727,
      y: 64,
    });

    const isAddPhotosPopUp = ref(false);
    const isAddFilesPopUp = ref(false);
    const currentChooserType = ref('');

    const { onUploadUpdate, uploadInfo } = UseUpload();
    const {
      flutterOpenCamera,
      flutterOpenFilePicker,
      flutterOpenImagePicker,
      flutterAddMedia,
    } = useFlutter();

    async function addMedia(e: Event) {
      if (props.customControl) {
        const fileList: FileList | null = (e.target as HTMLInputElement).files;
        emit('uploadPhotos', fileList);
        return;
      }
      const img: File = (e.target as HTMLInputElement).files![0];
      const isBmp = img.type.includes('bmp');
      let poster = '' as unknown | undefined | string;
      let converted = {} as IImageConverted;

      if (isBmp) {
        converted = await bmpConvertor(img);
      }

      if (!isBmp) {
        if (img.type.includes('video')) {
          const thumbnailBlob = await getVideoThumbnail(img, 1.0);
          poster = blobToUrl(thumbnailBlob);
        }
      }

      mediaToCrop.value = {
        url: isBmp ? converted.url : URL.createObjectURL(img),
        fileName: isBmp ? converted.name : img.name,
        fileType: isBmp ? converted.type : img.type,
        poster,
      };
      mediaLoadedKey.value += 1;
      mediaInputRef.value!.value = '';
    }

    watch(() => flutterBus.flutterCameraFile, (newValue) => {
      if (newValue) {
        flutterAddMedia(newValue).then((r) => {
          mediaToCrop.value = r;
        });
        flutterBus.flutterCameraFile = null;
      }
    });

    function setChooserType(chooserType) {
      currentChooserType.value = chooserType;
      const { nativeApp } = window as any;

      if (nativeApp && chooserType) {
        nativeApp.setChooserType(chooserType);
      }
    }

    function hidePopUp() {
      if (props.customControl) {
        emit('close');
        return;
      }
      mainStore.BottomPopUpType = null;
    }

    function hideDrivePopUp() {
      isAddPhotosPopUp.value = false;
      isAddFilesPopUp.value = false;
      hidePopUp();
    }

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
      appTakePhoto.value.resetAppTakePhoto();
    }
    const labels = ref([] as ISearchLabelInput[]);
    function handleLabels() {
      return mainStore.onSearchInputLabels('').then((response) => {
        labels.value = response;
        return response;
      });
    }

    function handleMultipleUpload(f: any) {
      const fileUploads = f.file as IFileUpload[];
      onUploadUpdate().setStartInfo(fileUploads);

      const caseId = driveStore.currentCase.id || undefined;
      const folderId = driveStore.currentCase.folderId || undefined;

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId, mainStore.recentInputLabels, true))
        .then((files) => {
          if (caseId) {
            const filesPayload = {
              id: caseId,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          }
        });
      hidePopUp();
    }

    function flutterUploadFiles(f: File[]) {
      // const fileUploads = f as IFileUpload[];
      const fileUploads = UploadService.toFileUploads(f);
      onUploadUpdate().setStartInfo(fileUploads);

      const caseId = driveStore.currentCase.id || undefined;
      const folderId = driveStore.currentCase.folderId || undefined;

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId, mainStore.recentInputLabels, true))
        .then((files) => {
          if (caseId) {
            const filesPayload = {
              id: caseId,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          }
        });
      hidePopUp();
    }

    watch(() => flutterBus.flutterImagePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadFiles(nv);
        flutterBus.flutterImagePickerFiles = null;
      }
    });

    watch(() => flutterBus.flutterFilePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadFiles(nv);
        flutterBus.flutterFilePickerFiles = null;
      }
    });

    function handleRetake() {
      setChooserType(currentChooserType.value);
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    function closeAddPhotos() {
      isAddPhotosPopUp.value = false;
      hidePopUp();
    }

    function closeAddFiles() {
      isAddFilesPopUp.value = false;
      hidePopUp();
    }

    function openAddPhotos() {
      if (props.customControl) {
        emit('addPhotos');
        return;
      }
      isAddPhotosPopUp.value = true;
    }

    // async function convertBmpInFileList(fileList: FileList) {
    //   const newFileList: File[] = [];
    //   for (let i = 0; i < fileList.length; i += 1) {
    //     if (fileList[i].type.includes('bmp')) {
    //       // eslint-disable-next-line no-await-in-loop
    //       const convertedFile = await bmpConvertorToFile(fileList[i]);
    //       newFileList.push(convertedFile);
    //     } else {
    //       newFileList.push(fileList[i]);
    //     }
    //   }
    //   return newFileList;
    // }
    async function uploadFiles(event: Event) {
      const fileList: FileList | null = (event.target as HTMLInputElement).files;

      if (props.customControl) {
        if (fileList) {
          const updatedFileList = await convertBmpInFileList(fileList);
          emit('uploadFiles', updatedFileList);
          return;
        }
        return;
      }

      if (fileList) {
        const newFileList = await convertBmpInFileList(fileList);
        const fileUploads = UploadService.toFileUploads(newFileList);
        const folderId = driveStore.currentCase.folderId || undefined;

        const id = driveStore.currentCase.id!;

        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE, id }, folderId)
          .then((files) => {
            const filesPayload = {
              id,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          });
        hidePopUp();
      }

      fileInput.value!.value = '';
    }

    function uploadPhotos(event: Event) {
      const fileList: FileList | null = (event.target as HTMLInputElement).files;

      if (props.customControl) {
        emit('uploadPhotos', fileList);
        return;
      }

      const caseId = driveStore.currentCase.id || undefined;

      if (fileList) {
        const fileUploads = UploadService.toFileUploads(fileList);
        const folderId = driveStore.currentCase.folderId || undefined;

        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId)
          .then((files) => {
            if (caseId) {
              const filesPayload = {
                id: caseId,
                fileIds: files.map((file) => file.id),
              } as ICaseFilesManage;

              driveStore.addFilesToCase(filesPayload);
            }
          });
        hidePopUp();
      }

      photoInput.value!.value = '';
    }

    function openAddFiles() {
      if (props.customControl) {
        emit('addFiles');
        return;
      }
      isAddFilesPopUp.value = true;
    }

    function cancelUpload() {
      hidePopUp();
      UploadService.isAbortUpload = true;
      handleMediaCancel();
    }

    return {
      fileInput,
      photoInput,
      router,
      uploadPhotos,
      hidePopUp,
      closeAddPhotos,
      closeAddFiles,
      openAddPhotos,
      MEDIA_TYPES,

      isAddPhotosPopUp,
      isAddFilesPopUp,
      mediaInputRef,
      mediaLoadedKey,
      mediaToCrop,
      appTakePhoto,
      labels,
      uploadInfo,
      addMedia,
      handleMediaCancel,
      handleRetake,
      handleMultipleUpload,
      cancelUpload,
      openAddFiles,
      uploadFiles,
      setChooserType,
      mainStore,
      flutterBus,
      positionData,
      hideDrivePopUp,
      CHOOSER_TYPE,
      posData,
      flutterOpenCamera,
      flutterOpenImagePicker,
      flutterOpenFilePicker,
    };
  },
});
